// Helper condivisi tra PlayersComparisonView (Confronto) e Top11View
// (Top 11 per modulo). Tutta la matematica del "refinement" (best-10 rating
// + Bayesian shrinkage) vive qui per evitare drift tra le due viste.

export const EA_POSITIONS = ["goalkeeper", "defender", "midfielder", "forward"] as const;
export type EaPosition = (typeof EA_POSITIONS)[number];

export type PeriodKey = "30" | "90" | "season" | "all";

// Tipo allineato all'output JSON della RPC team_player_position_stats dopo
// la migration 20260528000100_player_position_stats_top10_rating.
export type PositionStats = {
  player_id: string;
  display_name: string;
  role_code: string | null;
  is_guest: boolean;
  matches: number;
  goals: number;
  assists: number;
  mom: number;
  shots: number;
  passes_made: number;
  pass_attempts: number;
  pass_pct: number | null;
  tackles_made: number;
  tackle_attempts: number;
  tackle_pct: number | null;
  saves: number;
  goals_conceded: number;
  clean_sheets: number;
  red_cards: number;
  minutes_avg: number | null;
  wins: number;
  losses: number;
  draws: number;
  win_rate: number;
  avg_rating: number;
  top10_avg_rating: number;
  top10_used_matches: number;
  goals_per_match: number;
  assists_per_match: number;
  mom_per_match: number;
};

export type Benchmark = {
  avg_rating: number;
  win_rate: number;
  goals_per_match: number;
  assists_per_match: number;
  mom_per_match: number;
  pass_pct: number;
  tackle_pct: number;
};

export const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "#f59e0b",
  "#8b5cf6",
];

export function periodToDays(p: PeriodKey): number | null {
  if (p === "30") return 30;
  if (p === "90") return 90;
  if (p === "season") {
    // Stagione = dal 1 luglio dell'anno corrente (o precedente se prima di luglio).
    const now = new Date();
    const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    const start = new Date(year, 6, 1).getTime();
    return Math.max(1, Math.ceil((Date.now() - start) / (24 * 3600 * 1000)));
  }
  return null;
}

// Normalizzazione 0-100 per radar e score aggregato. I `cap` sono scelti per
// non far dominare i bomber sul radar e per dare scala leggibile.
export function normalize(v: number, cap: number): number {
  return Math.max(0, Math.min(100, (v / cap) * 100));
}

// ──────────────────────────────────────────────────────────────────────────
// Mix di "best 10 partite" + Bayesian shrinkage. Vedi compare.info.rating
// e compare.info.shrinkage per la spiegazione utente-facing.
// ──────────────────────────────────────────────────────────────────────────

export const SHRINKAGE_K = 10;
export const REFINEMENT_VISUAL_THRESHOLD = 15;
// Sopra questa soglia il "confidence factor" sullo score aggregato è 1.0
// (volume sufficiente per fidarsi pienamente del dato). A 10 match (minimo
// Top 11) il factor è 0.60; scala lineare fino a 1.0 a 50 match. Alzata da
// 30 a 50 per evitare che un attaccante con 35 match e media gol gonfia
// (sample piccolo) batta uno con 70 match e media più "vera".
export const CONFIDENCE_FULL_AT = 50;
// Confidenza minima: nessun player scende sotto 0.5 anche con sample = 1
// (il filtro min-match sul Top 11 / sample-low badge altrove proteggono il
// caso davvero patologico).
export const CONFIDENCE_FLOOR = 0.5;

export function shrinkValue(
  playerVal: number,
  n: number,
  teamVal: number | null | undefined,
  k = SHRINKAGE_K,
): number {
  if (teamVal == null || !Number.isFinite(teamVal) || n <= 0) return playerVal;
  return (n * playerVal + k * teamVal) / (n + k);
}

// Rating "raffinato": base = media dei top 10 rating (server-side), shrinkage
// verso team avg con n = numero TOTALE di match nel ruolo (non i 10 usati
// per il top, altrimenti player con 100 match e player con 10 si shrinkano
// allo stesso modo). Più match → meno shrinkage → rating più "suo".
export function effectiveRating(s: PositionStats, benchmark: Benchmark | null): number {
  const base = s.top10_avg_rating || s.avg_rating || 0;
  const n = Math.max(1, s.matches);
  return shrinkValue(base, n, benchmark?.avg_rating);
}

// Confidence sul volume di match: penalizza i player con sample piccolo
// nello score aggregato (Top 11, verdetto). Sotto CONFIDENCE_FULL_AT match
// il factor scala linearmente da CONFIDENCE_FLOOR a 1; sopra resta 1.
export function confidenceFactor(matches: number): number {
  if (matches >= CONFIDENCE_FULL_AT) return 1;
  if (matches <= 0) return CONFIDENCE_FLOOR;
  return CONFIDENCE_FLOOR + (1 - CONFIDENCE_FLOOR) * (matches / CONFIDENCE_FULL_AT);
}

export function refinedKpi(
  s: PositionStats,
  key: keyof Benchmark,
  benchmark: Benchmark | null,
): number {
  if (key === "avg_rating") return effectiveRating(s, benchmark);
  const raw = Number((s[key as keyof PositionStats] as number | null | undefined) ?? 0);
  return shrinkValue(raw, s.matches, benchmark?.[key]);
}

export function isRefinementVisible(s: PositionStats, key: keyof Benchmark): boolean {
  if (key === "avg_rating") {
    return s.matches > 10 || s.matches < REFINEMENT_VISUAL_THRESHOLD;
  }
  return s.matches < REFINEMENT_VISUAL_THRESHOLD;
}

export const BENCHMARK_KEYS: ReadonlySet<keyof PositionStats> = new Set([
  "avg_rating",
  "win_rate",
  "goals_per_match",
  "assists_per_match",
  "mom_per_match",
  "pass_pct",
  "tackle_pct",
]);

export function statValueOf(
  s: PositionStats,
  key: keyof PositionStats,
  benchmark: Benchmark | null,
): number {
  if (BENCHMARK_KEYS.has(key)) {
    return refinedKpi(s, key as keyof Benchmark, benchmark);
  }
  return Number((s[key] as number | null | undefined) ?? 0);
}

// ──────────────────────────────────────────────────────────────────────────
// Radar values + aggregate score. Usati sia dal confronto (radar) sia dal
// Top 11 (ranking dei player per macro-ruolo). I valori sono "raffinati" se
// è disponibile il benchmark del ruolo; senza benchmark si ricade su valori
// nudi (al primo paint, prima che la query benchmark abbia restituito).
// ──────────────────────────────────────────────────────────────────────────

export function radarValuesFor(
  s: PositionStats,
  position: EaPosition | "any",
  benchmark: Benchmark | null,
): number[] {
  const r = effectiveRating(s, benchmark);
  if (position === "goalkeeper") {
    return [
      normalize(r * 10, 100),
      refinedKpi(s, "win_rate", benchmark),
      normalize(s.matches > 0 ? (s.clean_sheets / s.matches) * 100 : 0, 100),
      normalize(s.matches > 0 ? s.saves / s.matches : 0, 6),
      refinedKpi(s, "mom_per_match", benchmark),
      normalize(s.minutes_avg ?? 0, 90),
    ];
  }
  if (position === "defender") {
    // Per il difensore le metriche specifiche sono tackle% e pass% (struttura
    // + duelli) invece di goals/match e assists/match. Assist/match resta
    // come piccolo bonus per il difensore che contribuisce in fase offensiva.
    return [
      normalize(r * 10, 100),
      refinedKpi(s, "win_rate", benchmark),
      refinedKpi(s, "tackle_pct", benchmark),
      refinedKpi(s, "pass_pct", benchmark),
      refinedKpi(s, "mom_per_match", benchmark),
      normalize(refinedKpi(s, "assists_per_match", benchmark), 1),
    ];
  }
  // Midfielder + Forward
  return [
    normalize(r * 10, 100),
    refinedKpi(s, "win_rate", benchmark),
    normalize(refinedKpi(s, "goals_per_match", benchmark), 1.5),
    normalize(refinedKpi(s, "assists_per_match", benchmark), 1),
    refinedKpi(s, "mom_per_match", benchmark),
    refinedKpi(s, "pass_pct", benchmark),
  ];
}

// Pesi role-aware per lo score aggregato (Top 11, Verdetto). Mappano sui
// 6 valori restituiti da radarValuesFor IN ORDINE:
//   • goalkeeper: [rating, win_rate, clean_sheets/m, saves/m, mom%, minutes_avg]
//   • defender:   [rating, win_rate, tackle%,        pass%,    mom%, assists/m]
//   • mid/fwd:    [rating, win_rate, goals/m,        assists/m, mom%, pass%]
// Somma = 1.0 per coerenza con uno score 0-100.
const SCORE_WEIGHTS: Record<EaPosition, [number, number, number, number, number, number]> = {
  goalkeeper: [0.35, 0.10, 0.25, 0.15, 0.10, 0.05],
  defender:   [0.40, 0.15, 0.20, 0.15, 0.05, 0.05],
  midfielder: [0.35, 0.10, 0.10, 0.20, 0.10, 0.15],
  forward:    [0.35, 0.10, 0.25, 0.15, 0.10, 0.05],
};

export function aggregateScore(
  s: PositionStats,
  position: EaPosition | "any",
  benchmark: Benchmark | null,
): number {
  if (position === "any") return 0;
  const vals = radarValuesFor(s, position, benchmark);
  if (vals.length === 0) return 0;
  const weights = SCORE_WEIGHTS[position];
  // Score pesato role-aware: il rating EA conta più di pass% per un attaccante,
  // i KPI specifici del ruolo (goals/m per fwd, pass% per mid, ecc.) sono
  // sovra-pesati rispetto a quelli generici.
  const weighted = vals.reduce((sum, v, i) => sum + v * weights[i], 0);
  // Confidence factor: scala lo score in base al numero di match. Un giocatore
  // con 80 match resta uguale, uno con 10 match (la soglia minima del Top 11)
  // scende a ~67% del suo score "teorico". Onesto: chi gioca tanto ha mostrato
  // di mantenere il livello, chi gioca poco ha un'altra prova da fare.
  return Math.round(weighted * confidenceFactor(s.matches));
}

// Affidabilità campione (low/medium/high). Usato per badge UI.
export function reliabilityOf(matches: number): "low" | "medium" | "high" {
  if (matches < 5) return "low";
  if (matches < 15) return "medium";
  return "high";
}

// Calcolo del benchmark di squadra (media semplice non pesata) a partire
// dalle stat di un set di player nella stessa posizione/periodo. Restituisce
// null se il set è vuoto.
export function computeBenchmark(rows: PositionStats[]): Benchmark | null {
  if (rows.length === 0) return null;
  const sum = rows.reduce(
    (acc, r) => ({
      avg_rating: acc.avg_rating + r.avg_rating,
      win_rate: acc.win_rate + r.win_rate,
      goals_per_match: acc.goals_per_match + r.goals_per_match,
      assists_per_match: acc.assists_per_match + r.assists_per_match,
      mom_per_match: acc.mom_per_match + r.mom_per_match,
      pass_pct: acc.pass_pct + (r.pass_pct ?? 0),
      tackle_pct: acc.tackle_pct + (r.tackle_pct ?? 0),
      n: acc.n + 1,
    }),
    { avg_rating: 0, win_rate: 0, goals_per_match: 0, assists_per_match: 0, mom_per_match: 0, pass_pct: 0, tackle_pct: 0, n: 0 },
  );
  const n = Math.max(1, sum.n);
  const r2 = (v: number) => Math.round(v * 100) / 100;
  return {
    avg_rating: r2(sum.avg_rating / n),
    win_rate: r2(sum.win_rate / n),
    goals_per_match: r2(sum.goals_per_match / n),
    assists_per_match: r2(sum.assists_per_match / n),
    mom_per_match: r2(sum.mom_per_match / n),
    pass_pct: r2(sum.pass_pct / n),
    tackle_pct: r2(sum.tackle_pct / n),
  };
}
